import type {
  Position,
  PositionedItem,
} from '../../PageConfigProvider/types/item';
import { MIN_ITEM_HEIGHT_CELLS, MIN_ITEM_WIDTH_CELLS } from '../constants';

export type OccupancyMatrix = boolean[][];

export type GridDimensions = {
  rows: number;
  columns: number;
};

type PlacementBounds = {
  yStart: number;
  yEnd: number;
};

type MaxNearestOffset = {
  columns: number;
  rows: number;
};

type GridPoint = {
  x: number;
  y: number;
};

export type DropPlacementKind = 'direct' | 'nearest' | 'resized';

export type DropPlacement = {
  kind: DropPlacementKind;
  position: Position;
};

type ResolveDropPlacementOptions = {
  grid: GridDimensions;
  desired: Position;
  items: PositionedItem[];
  ignoreItemId?: string;
  yBounds?: PlacementBounds;
  maxNearestOffset?: MaxNearestOffset;
  dropPoint?: GridPoint;
};

type OccupancyLookup = {
  prefixSums: number[][];
  freeRunRight: number[][];
};

function isIdIgnored(
  id: string,
  ignoreItemId?: string,
  ignoreItemIds?: Set<string> | string[]
): boolean {
  if (ignoreItemId && id === ignoreItemId) return true;
  if (ignoreItemIds) {
    if (ignoreItemIds instanceof Set) return ignoreItemIds.has(id);
    return ignoreItemIds.includes(id);
  }
  return false;
}

/**
 * Creates a 2D matrix representing the grid, marking cells occupied by items as `true`.
 */
export function buildOccupancyMatrix(
  grid: GridDimensions,
  items: PositionedItem[],
  ignoreItemId?: string,
  ignoreItemIds?: Set<string> | string[]
): OccupancyMatrix {
  const matrix: OccupancyMatrix = Array.from({ length: grid.rows }, () =>
    Array(grid.columns).fill(false)
  );

  items.forEach((it) => {
    if (isIdIgnored(it.id, ignoreItemId, ignoreItemIds)) return;
    occupyArea(it.position, matrix);
  });

  return matrix;
}

/**
 * Marks the cells within a given rectangle as occupied (`true`) in the matrix.
 */
export function occupyArea(rect: Position, matrix: OccupancyMatrix): void {
  for (let y = rect.y; y < rect.y + rect.height; y++) {
    for (let x = rect.x; x < rect.x + rect.width; x++) {
      if (
        y >= 0 &&
        y < matrix.length &&
        x >= 0 &&
        x < (matrix[y]?.length ?? 0)
      ) {
        // @ts-expect-error noUncheckedIndexedAccess
        matrix[y][x] = true;
      }
    }
  }
}

/**
 * Checks if a given rectangular area is completely free in the occupancy matrix.
 */
export function isAreaFree(
  rect: Position,
  grid: GridDimensions,
  matrix: boolean[][]
): boolean {
  if (
    rect.x < 0 ||
    rect.y < 0 ||
    rect.x + rect.width > grid.columns ||
    rect.y + rect.height > grid.rows
  ) {
    return false;
  }

  for (let y = rect.y; y < rect.y + rect.height; y++) {
    for (let x = rect.x; x < rect.x + rect.width; x++) {
      if (matrix[y]?.[x]) return false;
    }
  }
  return true;
}

/**
 * Validates that a position is within the grid boundaries and meets minimum size requirements.
 */
export function validateBoundaries(
  grid: GridDimensions,
  position: Position
): boolean {
  return (
    position.x >= 0 &&
    position.y >= 0 &&
    position.width >= MIN_ITEM_WIDTH_CELLS &&
    position.height >= MIN_ITEM_HEIGHT_CELLS &&
    position.x + position.width <= grid.columns &&
    position.y + position.height <= grid.rows
  );
}

/**
 * Validates a position against grid boundaries and for collisions with other items.
 */
export function validatePosition(
  grid: GridDimensions,
  position: Position,
  items: PositionedItem[],
  ignoreItemId?: string,
  ignoreItemIds?: Set<string> | string[]
): boolean {
  if (!validateBoundaries(grid, position)) {
    return false;
  }

  const matrix = buildOccupancyMatrix(grid, items, ignoreItemId, ignoreItemIds);
  if (!isAreaFree(position, grid, matrix)) {
    return false;
  }

  return true;
}

function buildOccupancyLookup(
  grid: GridDimensions,
  items: PositionedItem[],
  ignoreItemId?: string
): OccupancyLookup {
  const matrix = buildOccupancyMatrix(grid, items, ignoreItemId);
  const prefixSums: number[][] = Array.from({ length: grid.rows + 1 }, () =>
    Array(grid.columns + 1).fill(0)
  );
  const freeRunRight: number[][] = Array.from({ length: grid.rows }, () =>
    Array(grid.columns).fill(0)
  );

  for (let y = 1; y <= grid.rows; y++) {
    const prefixRow = prefixSums[y];
    const previousPrefixRow = prefixSums[y - 1];
    const occupancyRow = matrix[y - 1];
    if (!prefixRow || !previousPrefixRow || !occupancyRow) continue;

    for (let x = 1; x <= grid.columns; x++) {
      prefixRow[x] =
        (occupancyRow[x - 1] ? 1 : 0) +
        (previousPrefixRow[x] ?? 0) +
        (prefixRow[x - 1] ?? 0) -
        (previousPrefixRow[x - 1] ?? 0);
    }
  }

  for (let y = 0; y < grid.rows; y++) {
    const occupancyRow = matrix[y];
    const runRow = freeRunRight[y];
    if (!occupancyRow || !runRow) continue;

    for (let x = grid.columns - 1; x >= 0; x--) {
      runRow[x] = occupancyRow[x] ? 0 : (runRow[x + 1] ?? 0) + 1;
    }
  }

  return { prefixSums, freeRunRight };
}

function isAreaFreeInLookup(
  rect: Position,
  grid: GridDimensions,
  lookup: OccupancyLookup
): boolean {
  if (
    rect.x < 0 ||
    rect.y < 0 ||
    rect.x + rect.width > grid.columns ||
    rect.y + rect.height > grid.rows
  ) {
    return false;
  }

  const x1 = rect.x;
  const y1 = rect.y;
  const x2 = rect.x + rect.width;
  const y2 = rect.y + rect.height;

  const occupiedCellCount =
    (lookup.prefixSums[y2]?.[x2] ?? 0) -
    (lookup.prefixSums[y1]?.[x2] ?? 0) -
    (lookup.prefixSums[y2]?.[x1] ?? 0) +
    (lookup.prefixSums[y1]?.[x1] ?? 0);

  return occupiedCellCount === 0;
}

function isPositionFreeInLookup(
  position: Position,
  grid: GridDimensions,
  lookup: OccupancyLookup
): boolean {
  return (
    validateBoundaries(grid, position) &&
    isAreaFreeInLookup(position, grid, lookup)
  );
}

function isWithinPlacementBounds(
  position: Position,
  yBounds?: PlacementBounds
): boolean {
  if (!yBounds) return true;
  return (
    position.y >= yBounds.yStart && position.y + position.height <= yBounds.yEnd
  );
}

function positionDistance(a: Position, b: Position): number {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}

function containsGridPoint(position: Position, point: GridPoint): boolean {
  return (
    point.x >= position.x &&
    point.x < position.x + position.width &&
    point.y >= position.y &&
    point.y < position.y + position.height
  );
}

function positionDistanceToPoint(
  position: Position,
  point: GridPoint
): {
  columns: number;
  rows: number;
} {
  const columns = (() => {
    if (point.x < position.x) return position.x - point.x;
    if (point.x >= position.x + position.width) {
      return point.x - (position.x + position.width - 1);
    }
    return 0;
  })();

  const rows = (() => {
    if (point.y < position.y) return position.y - point.y;
    if (point.y >= position.y + position.height) {
      return point.y - (position.y + position.height - 1);
    }
    return 0;
  })();

  return { columns, rows };
}

function placementDistance(
  desired: Position,
  candidate: Position,
  dropPoint?: GridPoint
): number {
  if (!dropPoint) return positionDistance(desired, candidate);

  const distance = positionDistanceToPoint(candidate, dropPoint);
  return distance.columns + distance.rows;
}

function isWithinNearestOffset(
  desired: Position,
  candidate: Position,
  maxNearestOffset?: MaxNearestOffset,
  dropPoint?: GridPoint
): boolean {
  if (!maxNearestOffset) return true;
  if (dropPoint) {
    const distance = positionDistanceToPoint(candidate, dropPoint);
    return (
      distance.columns <= maxNearestOffset.columns &&
      distance.rows <= maxNearestOffset.rows
    );
  }
  return (
    Math.abs(candidate.x - desired.x) <= maxNearestOffset.columns &&
    Math.abs(candidate.y - desired.y) <= maxNearestOffset.rows
  );
}

function findClosestValidPosition(
  { grid, desired, yBounds, dropPoint }: ResolveDropPlacementOptions,
  lookup: OccupancyLookup
): Position | null {
  let best: Position | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  const minY = Math.max(0, yBounds?.yStart ?? 0);
  const maxY = Math.min(grid.rows, yBounds?.yEnd ?? grid.rows);
  const lastStartY = maxY - desired.height;

  for (let y = minY; y <= lastStartY; y++) {
    for (let x = 0; x <= grid.columns - desired.width; x++) {
      const candidate: Position = {
        x,
        y,
        width: desired.width,
        height: desired.height,
      };

      if (!isPositionFreeInLookup(candidate, grid, lookup)) continue;

      const distance = placementDistance(desired, candidate, dropPoint);

      if (distance < bestDistance) {
        bestDistance = distance;
        best = candidate;
        if (distance === 0) return best;
      }
    }
  }

  return best;
}

type ResizeCandidateScore = {
  containsDropPoint: boolean;
  isNearDrop: boolean;
  area: number;
  aspectDelta: number;
  distance: number;
};

function isBetterResizeCandidate(
  score: ResizeCandidateScore,
  bestScore: ResizeCandidateScore | null
): boolean {
  if (!bestScore) return true;
  if (score.containsDropPoint !== bestScore.containsDropPoint) {
    return score.containsDropPoint;
  }
  if (score.isNearDrop !== bestScore.isNearDrop) return score.isNearDrop;
  if (score.area !== bestScore.area) return score.area > bestScore.area;
  if (score.aspectDelta !== bestScore.aspectDelta) {
    return score.aspectDelta < bestScore.aspectDelta;
  }
  return score.distance < bestScore.distance;
}

function findBestResizedPosition(
  {
    grid,
    desired,
    yBounds,
    maxNearestOffset,
    dropPoint,
  }: ResolveDropPlacementOptions,
  lookup: OccupancyLookup
): Position | null {
  const maxWidth = Math.min(desired.width, grid.columns);
  const maxHeight = Math.min(
    desired.height,
    yBounds ? yBounds.yEnd - yBounds.yStart : grid.rows
  );
  const originalAspectRatio = desired.width / desired.height;

  let best: Position | null = null;
  let bestScore: ResizeCandidateScore | null = null;

  if (maxWidth < MIN_ITEM_WIDTH_CELLS || maxHeight < MIN_ITEM_HEIGHT_CELLS) {
    return null;
  }

  const minY = Math.max(0, yBounds?.yStart ?? 0);
  const maxY = Math.min(grid.rows, yBounds?.yEnd ?? grid.rows);
  const lastStartY = maxY - MIN_ITEM_HEIGHT_CELLS;

  for (let y = minY; y <= lastStartY; y++) {
    const topRowRun = lookup.freeRunRight[y];
    if (!topRowRun) continue;

    for (let x = 0; x <= grid.columns - MIN_ITEM_WIDTH_CELLS; x++) {
      let availableWidth = Math.min(topRowRun[x] ?? 0, maxWidth);
      if (availableWidth < MIN_ITEM_WIDTH_CELLS) continue;

      const candidateMaxHeight = Math.min(maxHeight, maxY - y);
      for (let height = 1; height <= candidateMaxHeight; height++) {
        const rowRun = lookup.freeRunRight[y + height - 1];
        if (!rowRun) break;

        availableWidth = Math.min(availableWidth, rowRun[x] ?? 0, maxWidth);
        if (availableWidth < MIN_ITEM_WIDTH_CELLS) break;
        if (height < MIN_ITEM_HEIGHT_CELLS) continue;

        const candidate: Position = {
          x,
          y,
          width: availableWidth,
          height,
        };

        if (
          candidate.width === desired.width &&
          candidate.height === desired.height
        ) {
          continue;
        }

        if (!isWithinPlacementBounds(candidate, yBounds)) continue;

        const score: ResizeCandidateScore = {
          containsDropPoint: dropPoint
            ? containsGridPoint(candidate, dropPoint)
            : false,
          isNearDrop: isWithinNearestOffset(
            desired,
            candidate,
            maxNearestOffset,
            dropPoint
          ),
          area: candidate.width * candidate.height,
          aspectDelta: Math.abs(
            candidate.width / candidate.height - originalAspectRatio
          ),
          distance: placementDistance(desired, candidate, dropPoint),
        };

        if (isBetterResizeCandidate(score, bestScore)) {
          best = candidate;
          bestScore = score;
        }
      }
    }
  }

  return best;
}

export function resolveDropPlacement(
  options: ResolveDropPlacementOptions
): DropPlacement | null {
  const {
    grid,
    desired,
    items,
    ignoreItemId,
    yBounds,
    maxNearestOffset,
    dropPoint,
  } = options;
  const lookup = buildOccupancyLookup(grid, items, ignoreItemId);

  if (
    isWithinPlacementBounds(desired, yBounds) &&
    isPositionFreeInLookup(desired, grid, lookup)
  ) {
    return { kind: 'direct', position: desired };
  }

  const nearest = findClosestValidPosition(options, lookup);
  if (
    nearest &&
    (!dropPoint || containsGridPoint(nearest, dropPoint)) &&
    isWithinNearestOffset(desired, nearest, maxNearestOffset, dropPoint)
  ) {
    return { kind: 'nearest', position: nearest };
  }

  const resized = findBestResizedPosition(options, lookup);
  if (resized) {
    return { kind: 'resized', position: resized };
  }

  if (nearest) {
    return { kind: 'nearest', position: nearest };
  }

  return null;
}
